
import moment from 'moment'
import { mapState, mapMutations } from 'vuex'

export default {
  name: 'CompleteProfileModal',
  data() {
    return {
      month: null,
      day: null,
      year: null,
      emailOptin: false,
      smsOptin: false,
      email: '',
      phone: '',
      invalidBirthdate: false,
      loading: false
    }
  },
  computed: {
    ...mapState(['showCompleteProfileModal', 'user', 'channel']),
    daysInMonth() {
      if (this.month) {
        return moment().month(this.month - 1).daysInMonth()
      }
      return 31
    },
    years() {
      const currentYear = new Date().getFullYear()
      return Array.from({ length: 100 }, (_, i) => currentYear - i - 8)
    },
    canSave() {
      // Button is enabled if:
      // 1. At least one opt-in is selected (email or SMS), OR
      // 2. Birthday is provided (optional but can be saved alone)
      const hasOptIn = this.emailOptin || this.smsOptin
      const hasBirthday = this.month && this.day && this.year

      // If email opt-in is selected, email must be provided
      if (this.emailOptin && !this.email) {
        return false
      }

      // If SMS opt-in is selected, phone must be provided
      if (this.smsOptin && !this.phone) {
        return false
      }

      // At least one field must be filled (opt-in or birthday)
      return hasOptIn || hasBirthday
    }
  },
  watch: {
    showCompleteProfileModal(val) {
      if (val && this.user) {
        // Pre-fill email if user is logged in
        this.email = this.user.email || ''
        this.phone = this.user.phone || ''

        // Pre-check opt-ins if user is already opted in (but still allow them to opt out)
        // Note: We show the modal even if opted in, but pre-fill their preferences
        if (this.user.optin) {
          this.emailOptin = true
        }
        if (this.user.optin_sms) {
          this.smsOptin = true
        }
      }
    }
  },
  methods: {
    ...mapMutations(['setShowCompleteProfileModal', 'setLoading']),
    formatMonth(i) {
      return moment().month(i - 1).format('MMM')
    },
    closeModal() {
      this.setShowCompleteProfileModal(false)
      this.resetForm()
      
      // Clear the new user flag so modal won't show again in this session
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('boj_isNewUser')
        console.log('[CompleteProfileModal] ✅ Cleared new user flag on close')
      }
    },
    resetForm() {
      this.month = null
      this.day = null
      this.year = null
      this.emailOptin = false
      this.smsOptin = false
      this.email = ''
      this.phone = ''
      this.invalidBirthdate = false
    },
    async saveProfile() {
      this.invalidBirthdate = false
      this.loading = true

      try {
        // Validate birthday if provided
        if (this.month && this.day && this.year) {
          const dob = `${this.year}-${String(this.month).padStart(2, '0')}-${String(this.day).padStart(2, '0')}`
          if (!moment(dob, 'YYYY-MM-DD', true).isValid()) {
            this.invalidBirthdate = true
            this.loading = false
            return
          }
          // Update birthday in Braze
          this.$braze.getUser().setDateOfBirth(
            this.year,
            String(this.month).padStart(2, '0'),
            String(this.day).padStart(2, '0')
          )
        }

        // Update email opt-in in Braze
        if (this.emailOptin && this.email) {
          this.$braze.getUser().setEmail(this.email)
          this.$braze.getUser().setEmailNotificationSubscriptionType('opted_in')
        }

        // Update SMS opt-in in Braze
        if (this.smsOptin && this.phone) {
          // Format phone number for Braze (E.164 format)
          const cleanPhone = this.phone.replace(/\D/g, '')
          const formattedPhone = cleanPhone.length === 10 ? `+1${cleanPhone}` : `+${cleanPhone}`
          this.$braze.getUser().setPhoneNumber(formattedPhone)

          // Add to SMS subscription group
          if (process.env.BRAZE_SMS_SUBSCRIPTION_GROUP) {
            this.$braze.getUser().addToSubscriptionGroup(process.env.BRAZE_SMS_SUBSCRIPTION_GROUP)
          }
        }

        // Update user in Olo backend (to sync DOB with backend)
        if (this.user) {
          const updatePayload = {
            first_name: this.user.first_name,
            last_name: this.user.last_name,
            email: this.email || this.user.email,
            phone: this.phone ? this.phone.replace(/\D/g, '') : this.user.phone,
            optin: this.emailOptin,
            optin_sms: this.smsOptin
          }

          // Add DOB if provided
          if (this.month && this.day && this.year) {
            updatePayload.dob = `${String(this.month).padStart(2, '0')}/${String(this.day).padStart(2, '0')}/${this.year}`
          }

          const updatedUser = await this.$api.updateUser(updatePayload)
          
          // Update Vuex with the updated user data (including DOB)
          if (updatedUser) {
            this.$store.commit('setUser', updatedUser)
          }
        }

        // Log custom event for profile completion
        const eventName = this.channel === 'catering'
          ? 'Profile_Completed_Catering'
          : 'Profile_Completed'

        this.$braze.logCustomEvent(eventName, {
          has_birthday: !!(this.month && this.day && this.year),
          email_opted_in: this.emailOptin,
          sms_opted_in: this.smsOptin
        })

        // Clear the new user flag so modal won't show again in this session
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('boj_isNewUser')
          console.log('[CompleteProfileModal] ✅ Cleared new user flag - modal won\'t show again')
        }

        // Close modal
        this.closeModal()

      } catch (error) {
        console.error('[CompleteProfileModal] Error saving profile:', error)
        this.$store.commit('setErrors', ['There was an error saving your profile. Please try again.'])
      } finally {
        this.loading = false
      }
    }
  }
}
